import type { Day, Locale } from "date-fns";
import { eachDayOfInterval, endOfWeek, format, startOfWeek } from "date-fns";
import { useMemo } from "react";

type UseCalendarDaysOptions = {
  adapterLocale: Locale;
  monthStart: Date;
  monthEnd: Date;
  firstDayOfWeek?: string | null;
};

export default function useCalendarDays({
  adapterLocale,
  monthStart,
  monthEnd,
  firstDayOfWeek,
}: UseCalendarDaysOptions) {
  const weekStartsOn = useMemo<Day>(() => {
    if (firstDayOfWeek) {
      const weekdays = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const index = weekdays.indexOf(firstDayOfWeek);
      if (index >= 0) return index as Day;
    }
    return (adapterLocale.options?.weekStartsOn ?? 0) as Day;
  }, [adapterLocale.options?.weekStartsOn, firstDayOfWeek]);

  const calendarStart = useMemo(
    () => startOfWeek(monthStart, { weekStartsOn }),
    [monthStart, weekStartsOn],
  );
  const calendarEnd = useMemo(
    () => endOfWeek(monthEnd, { weekStartsOn }),
    [monthEnd, weekStartsOn],
  );

  const calendarDays = useMemo(
    () => eachDayOfInterval({ start: calendarStart, end: calendarEnd }),
    [calendarEnd, calendarStart],
  );

  const weekdayLabels = useMemo(() => {
    const weekEnd = endOfWeek(calendarStart, { weekStartsOn });
    return eachDayOfInterval({ start: calendarStart, end: weekEnd }).map(
      (day) => format(day, "EEE", { locale: adapterLocale }),
    );
  }, [adapterLocale, calendarStart, weekStartsOn]);

  const weeksInView = useMemo(
    () => Math.max(1, Math.round(calendarDays.length / 7)),
    [calendarDays.length],
  );

  return {
    calendarDays,
    weekdayLabels,
    weeksInView,
  };
}
